// components/Reports.tsx — unified transactions page (mobile money + cards in one)
import React, { useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import TransactionsReport from './reports/TransactionsReport';
import CardTransactions from './CardTransactions';
import { Smartphone, CreditCard } from 'lucide-react';

type Channel = 'mobile' | 'cards';

const Reports: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const activeChannel: Channel = searchParams.get('tab') === 'cards' ? 'cards' : 'mobile';

  const setChannel = (c: Channel) => {
    const next = new URLSearchParams(searchParams);
    if (c === 'mobile') next.delete('tab');
    else next.set('tab', c);
    setSearchParams(next, { replace: true });
    // Make sure we stay on /reports/transactions (older bookmarks may land on /reports/overview)
    if (window.location.pathname !== '/reports/transactions') {
      navigate(`/reports/transactions${c === 'cards' ? '?tab=cards' : ''}`, { replace: true });
    }
  };

  const channels = useMemo(
    () => [
      { id: 'mobile' as const, label: 'Mobile Money', icon: Smartphone },
      { id: 'cards'  as const, label: 'Cards',         icon: CreditCard },
    ],
    []
  );

  return (
    <div className="max-w-7xl mx-auto space-y-5">
      {/* Header — Metronic-style */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 pb-2">
        <div>
          <h1 className="text-[22px] font-semibold text-gray-900 tracking-tight">Transactions</h1>
          <p className="text-sm text-gray-500 mt-1">Search, filter and export your transactions</p>
        </div>
      </div>

      {/* Channel tabs */}
      <div className="border-b border-gray-200">
        <div className="flex items-center gap-1">
          {channels.map((c) => {
            const Icon = c.icon;
            const active = activeChannel === c.id;
            return (
              <button
                key={c.id}
                onClick={() => setChannel(c.id)}
                className={`inline-flex items-center gap-1.5 px-3.5 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  active
                    ? 'border-emerald-600 text-emerald-700'
                    : 'border-transparent text-gray-500 hover:text-gray-800'
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
                {c.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Body */}
      <div>{activeChannel === 'mobile' ? <TransactionsReport /> : <CardTransactions />}</div>
    </div>
  );
};

export default Reports;
